import type {RoadmapItem} from './roadmapData';
import type {RoadmapGrouping, RoadmapHierarchyNode} from './types';

export interface RoadmapGroupingOption {
  value: RoadmapGrouping;
  label: string;
}

export const getRoadmapHierarchyDepths = (items: RoadmapItem[]): Map<string, number> => {
  const itemsByKey = new Map(items.map((item) => [item.key, item]));
  const depths = new Map<string, number>();
  const visiting = new Set<string>();

  const visit = (item: RoadmapItem): number => {
    const cached = depths.get(item.key);
    if (cached !== undefined) return cached;
    if (visiting.has(item.key)) return 0;

    visiting.add(item.key);
    const parent = item.parentKey ? itemsByKey.get(item.parentKey) : null;
    const depth = parent ? visit(parent) + 1 : 0;
    visiting.delete(item.key);
    depths.set(item.key, depth);
    return depth;
  };

  items.forEach(visit);
  return depths;
};

export const buildRoadmapGroupingOptions = (items: RoadmapItem[]): RoadmapGroupingOption[] => {
  const depths = getRoadmapHierarchyDepths(items);
  const maxDepth = Math.max(-1, ...depths.values());
  const options: RoadmapGroupingOption[] = [];

  for (let depth = 0; depth < maxDepth; depth += 1) {
    options.push({
      value: `depth:${depth}`,
      label: depth === 0 ? 'Top level' : `Top ${depth + 1} levels`,
    });
  }

  return [...options, {value: 'all', label: 'All hierarchy levels'}];
};

export const resolveRoadmapGrouping = (
  requested: RoadmapGrouping,
  options: RoadmapGroupingOption[]
): RoadmapGrouping => {
  if (options.some((option) => option.value === requested)) return requested;
  return options.at(-1)?.value ?? 'all';
};

export const getRoadmapGroupingMaxDepth = (grouping: RoadmapGrouping): number => {
  if (grouping === 'all') return Number.POSITIVE_INFINITY;
  const depth = Number(grouping.slice('depth:'.length));
  return Number.isInteger(depth) && depth >= 0 ? depth : Number.POSITIVE_INFINITY;
};

const hasWorkstreamOrder = (item: RoadmapItem) =>
  typeof item.workstreamOrder === 'number' && Number.isFinite(item.workstreamOrder);

export const sortRoadmapHierarchyItems = (left: RoadmapItem, right: RoadmapItem): number => {
  const archivedComparison =
    Number(left.workstreamArchived === true) - Number(right.workstreamArchived === true);
  if (archivedComparison !== 0) return archivedComparison;

  const leftHasOrder = hasWorkstreamOrder(left);
  const rightHasOrder = hasWorkstreamOrder(right);
  if (leftHasOrder && rightHasOrder && left.workstreamOrder !== right.workstreamOrder) {
    return (left.workstreamOrder ?? 0) - (right.workstreamOrder ?? 0);
  }
  if (leftHasOrder !== rightHasOrder) return leftHasOrder ? -1 : 1;

  return (
    left.title.localeCompare(right.title, undefined, {numeric: true}) ||
    left.key.localeCompare(right.key, undefined, {numeric: true})
  );
};

export const prependCurrentScopeInitiatives = (
  nodes: RoadmapHierarchyNode[],
  currentScope: RoadmapItem | null,
  initiatives: RoadmapItem[]
): RoadmapHierarchyNode[] => {
  if (!currentScope || initiatives.length === 0) return nodes;

  return [
    {
      key: currentScope.key,
      title: currentScope.title,
      issueType: currentScope.issueType,
      workstreamId: currentScope.workstreamId,
      workstreamArchived: currentScope.workstreamArchived,
      children: [],
      initiatives,
    },
    ...nodes,
  ];
};

export const pruneEmptyRoadmapHierarchy = (nodes: RoadmapHierarchyNode[]): RoadmapHierarchyNode[] =>
  nodes.flatMap((node) => {
    const children = pruneEmptyRoadmapHierarchy(node.children);
    if (node.initiatives.length === 0 && children.length === 0) return [];
    return [{...node, children}];
  });

export const pruneArchivedEmptyRoadmapHierarchy = (
  nodes: RoadmapHierarchyNode[]
): RoadmapHierarchyNode[] =>
  nodes.flatMap((node) => {
    const children = pruneArchivedEmptyRoadmapHierarchy(node.children);
    if (node.workstreamArchived === true && node.initiatives.length === 0) return children;
    return [{...node, children}];
  });
